import { createRouter, createWebHistory } from "vue-router";

/* Layouts */
import AppLayout from "@/layouts/AppLayout.vue";
import PublicLayout from "@/layouts/PublicLayout.vue";

/* Public */
import Login from "@/views/outros/Login.vue";
import CriarConta from "@/views/outros/CriarConta.vue";
import RecuperarSenha from "@/views/outros/RecuperarSenha.vue";
import DefinirSenhaGoogle from "@/views/outros/DefinirSenhaGoogle.vue";
import CheckoutSucesso from "@/views/outros/CheckoutSucesso.vue";
import LandingPage from "@/views/landingPage/LandingPage.vue";

/* Termos e Privacidade */
import Termos from "@/views/termos/Termos.vue";
import Privacidade from "@/views/termos/Privacidade.vue";

/* Admin */
import Admin from "@/views/dashboard/Admin.vue";

/* App */
import MeusProjetos from "@/views/projetos/MeusProjetos.vue";
import ComponentesProjeto from "@/views/componentes/ComponentesProjeto.vue";
import Assinatura from "@/views/assinatura/Assinatura.vue";
import EditarUsuario from "@/views/usuarios/EditarUsuario.vue";
import AlterarSenha from "@/views/usuarios/AlterarSenha.vue";
import Suporte from "@/views/outros/Suporte.vue";

/* Calculadoras */
import CalculatorDesignData from "@/views/calculadoras/CalculatorDesignData.vue";
import CalculatorBocal from "@/views/calculadoras/CalculatorBocal.vue";
import CalculatorFlangeIntegral from "@/views/calculadoras/CalculatorFlangeIntegral.vue";
import CalculatorShellCilindInternalPress from "@/views/calculadoras/CalculatorShellCilindInternalPress.vue";
import CalculatorShellConicalInternalPress from "@/views/calculadoras/CalculatorShellConicalInternalPress.vue";
import CalculatorSphericalShellsPressConcave from "@/views/calculadoras/CalculatorSphericalShellsPressConcave.vue";
import CalculatorEllipsoidalHeadPressConcave from "@/views/calculadoras/CalculatorEllipsoidalHeadPressConcave.vue";
import CalculatorTorispleHeadPressConcave from "@/views/calculadoras/CalculatorTorispleHeadPressConcave.vue";
import { carregarUsuarioAtual, clearUsuario, getTipoUsuario, getUsuario } from "@/utils/authStorage";

const routes = [
  /* 🌍 Público */
  {
    path: "/",
    component: PublicLayout,
    children: [
      { path: "", component: LandingPage, meta: { title: "Conheça a WiseCalc" } },
      { path: "login", component: Login, meta: { title: "Login - WiseCalc" } },
      { path: "create-account", component: CriarConta, meta: { title: "Criar Conta - WiseCalc" } },
      { path: "recover-password", component: RecuperarSenha, meta: { title: "Recuperar Senha - WiseCalc" } },
      { path: "set-password", component: DefinirSenhaGoogle, meta: { title: "Definir Senha - WiseCalc" } },
      { path: "checkout-success", component: CheckoutSucesso, meta: { title: "Cadastro concluído - WiseCalc" } },
      // Termos e Privacidade
      { path: "terms", component: Termos, meta: { title: "Termos - WiseCalc" } },
      { path: "privacy", component: Privacidade, meta: { title: "Privacidade - WiseCalc" } },
    ],
  },

  /* 🔒 App */
  {
    path: "/app",
    component: AppLayout,
    meta: { requiresAuth: true },
    children: [
      { path: "admin", component: Admin, meta: { title: "Admin - WiseCalc", requiresAdmin: true } },
      { path: "", component: MeusProjetos, meta: { title: "Meus Projetos - WiseCalc" } },
      { path: "project/:cod", component: ComponentesProjeto, props: true, meta: { title: "Componentes do Projeto - WiseCalc" } },
      { path: "my-plan", component: Assinatura, meta: { title: "Minha Assinatura - WiseCalc", subscriptionExempt: true } },
      { path: "edit-user", component: EditarUsuario, meta: { title: "Editar Usuário - WiseCalc" } },
      { path: "change-password", component: AlterarSenha, meta: { title: "Alterar Senha - WiseCalc" } },
      { path: "support", component: Suporte, meta: { title: "Suporte - WiseCalc" } },

      /* Calculadoras */
      { path: "calculator-design-data/:cod", component: CalculatorDesignData, props: true, meta: { title: "Design Data - WiseCalc" } },
      { path: "calculator-bocal/:cod", component: CalculatorBocal, props: true, meta: { title: "Bocal - WiseCalc" } },
      { path: "calculator-flange-integral/:cod", component: CalculatorFlangeIntegral, props: true, meta: { title: "Flange Integral - WiseCalc" } },
      { path: "calculator-shell-cilind-internal-press/:cod", component: CalculatorShellCilindInternalPress, props: true, meta: { title: "Shell Cilind - WiseCalc" } },
      { path: "calculator-shell-conical-internal-press/:cod", component: CalculatorShellConicalInternalPress, props: true, meta: { title: "Shell Conical - WiseCalc" } },
      { path: "calculator-spherical-shells-press-concave/:cod", component: CalculatorSphericalShellsPressConcave, props: true, meta: { title: "Shell Spherical - WiseCalc" } },
      { path: "calculator-ellipsoidal-head-press-concave/:cod", component: CalculatorEllipsoidalHeadPressConcave, props: true, meta: { title: "Elipsoidal - WiseCalc" } },
      { path: "calculator-torisple-head-press-concave/:cod", component: CalculatorTorispleHeadPressConcave, props: true, meta: { title: "Torisple - WiseCalc" } },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

/* 🔐 Guard */
router.beforeEach(async (to, from, next) => {

  const usuarioParsed = to.path === "/" || to.meta.requiresAuth
    ? await carregarUsuarioAtual()
    : getUsuario();

  // Se acessar "/" e estiver logado, vai direto pro app
  if (to.path === "/" && usuarioParsed) {
    return next("/app");
  }

  // Se rota exige login e não está logado
  if (to.meta.requiresAuth && !usuarioParsed) {
    return next("/login");
  }

  const acessoLivre = to.matched.some((route) => route.meta.subscriptionExempt);
  const tipoUsuario = usuarioParsed ? getTipoUsuario() : null;

  if (to.meta.requiresAuth && usuarioParsed && !tipoUsuario) {
    clearUsuario();
    return next("/login");
  }

  const eAdministrador = tipoUsuario === "admin";

  if (to.meta.requiresAdmin && !eAdministrador) {
    return next("/app");
  }

  if (to.meta.requiresAuth && eAdministrador) {
    return next();
  }

  if (to.meta.requiresAuth && usuarioParsed && !acessoLivre) {
    try {
      const response = await fetch(`${process.env.VUE_APP_BACKEND_URL}/consultar-status-assinatura`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: usuarioParsed.email })
      });

      if (!response.ok) {
        return next({ path: "/app/my-plan", query: { bloqueio: "indisponivel" } });
      }

      const {
        status,
        cancelado_ultimo_mes: cancelado,
        data_fim_unix: dataFimUnix
      } = await response.json();
      const assinaturaCancelada = status === "canceled" || cancelado === true;
      const fimAssinatura = dataFimUnix
        ? (dataFimUnix > 9999999999 ? dataFimUnix : dataFimUnix * 1000)
        : 0;
      const acessoAteOFimDoPeriodo = assinaturaCancelada && fimAssinatura > Date.now();
      const podeAcessar = status === "trialing"
        || status === "vitalicia"
        || (["estudante", "fundador"].includes(status) && dataFimUnix > Math.floor(Date.now() / 1000))
        || (status === "active" && cancelado !== true)
        || acessoAteOFimDoPeriodo;

      if (!podeAcessar) {
        const bloqueio = status === "past_due"
          ? "pagamento-atrasado"
          : cancelado
            ? "cancelada"
            : "sem-assinatura";

        return next({ path: "/app/my-plan", query: { bloqueio } });
      }
    } catch (error) {
      console.error("Não foi possível verificar a assinatura:", error);
      return next({ path: "/app/my-plan", query: { bloqueio: "indisponivel" } });
    }
  }

  next();
});

/* 🧠 Título da página (ideal p/ GA) */
router.afterEach((to) => {
  document.title = to.meta.title || "WiseCalc";
});

export default router;
